import { useState } from "react";
import { POSITION_TYPE_LABEL, fmtEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { DecimalField } from "@/components/DecimalInput";
import { AutoTextarea } from "@/components/AutoTextarea";
import { PRICE_OPTS, QTY_OPTS, RABATT_OPTS, parseDecimal } from "@/lib/decimal";
import { TD, TD_NUM, TD_STATIC, BESCHREIBUNG_MAX } from "@/components/PositionRow";

const EMPTY = { typ: "3d_druck", beschreibung: "", material: "", farbe: "", menge: "1", preis: "", rabatt: "" };

export function NewPositionRow({ onAdd }) {
  const [f, setF] = useState(EMPTY);
  const [err, setErr] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v })); setErr((e) => ({ ...e, [k]: "" })); };
  const check = (k, opts) => { const r = parseDecimal(f[k], opts); setErr((e) => ({ ...e, [k]: r.error })); return r; };
  const preis = parseDecimal(f.preis, PRICE_OPTS);

  const submit = async () => {
    const m = check("menge", QTY_OPTS);
    const pr = check("preis", PRICE_OPTS);
    const rb = check("rabatt", RABATT_OPTS);
    const beschrErr = f.beschreibung.trim() ? "" : "Beschreibung erforderlich.";
    setErr({ menge: m.error, preis: pr.error, rabatt: rb.error, beschreibung: beschrErr });
    if (m.error || pr.error || rb.error || beschrErr) return;
    setSaving(true);
    const ok = await onAdd({ typ: f.typ, beschreibung: f.beschreibung, material: f.material, farbe: f.farbe,
      menge: m.value, einzelpreis_netto: pr.value, rabatt_prozent: rb.value ?? 0, mwst_satz: 19, status: "neu" });
    setSaving(false);
    if (ok) { setF(EMPTY); setErr({}); }
  };
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } };

  return (
    <tr className="bg-slate-50" data-testid="new-position-row">
      <td className={TD}>
        <Select value={f.typ} onValueChange={set("typ")}>
          <SelectTrigger className="h-8 min-w-[130px]" data-testid="newpos-typ"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(POSITION_TYPE_LABEL).filter(([k]) => k !== "versand").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className={TD}>
        {/* Enter = neue Zeile (kein Absenden); Strg/⌘+Enter fügt die Position hinzu */}
        <AutoTextarea className={err.beschreibung ? "border-rose-500 bg-rose-50" : ""} placeholder="Beschreibung…" title="Enter = neue Zeile · Strg/⌘+Enter = hinzufügen" maxLength={BESCHREIBUNG_MAX} value={f.beschreibung}
          onChange={(e) => set("beschreibung")(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          aria-label="Beschreibung der neuen Position" aria-invalid={!!err.beschreibung} data-testid="newpos-beschr" />
        {err.beschreibung && <p className="text-[10px] text-rose-600 mt-0.5" role="alert" data-testid="newpos-beschr-error">{err.beschreibung}</p>}
      </td>
      <td className={TD}><Input className="h-8 w-28" placeholder="z.B. PLA" value={f.material} onChange={(e) => set("material")(e.target.value)} onKeyDown={onKey} aria-label="Material der neuen Position" data-testid="newpos-material" /></td>
      <td className={TD}><Input className="h-8 w-24" placeholder="Farbe" value={f.farbe} onChange={(e) => set("farbe")(e.target.value)} onKeyDown={onKey} aria-label="Farbe der neuen Position" data-testid="newpos-farbe" /></td>
      <td className={TD_NUM}><DecimalField integer className="h-8 w-20" value={f.menge} onChange={set("menge")} error={err.menge} onBlur={() => f.menge && check("menge", QTY_OPTS)} onKeyDown={onKey} aria-label="Menge der neuen Position" testId="newpos-menge" /></td>
      <td className={TD_NUM}>
        <DecimalField className="h-8 w-28" placeholder="0,00" value={f.preis} onChange={set("preis")} error={err.preis} onBlur={() => f.preis && check("preis", PRICE_OPTS)} onKeyDown={onKey}
          aria-label="Einzelpreis netto der neuen Position" testId="newpos-preis"
          hint={<span className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap" data-testid="newpos-preis-brutto">{preis.error ? "netto" : `brutto ${fmtEUR(preis.value * 1.19)}`}</span>} />
      </td>
      <td className={TD_NUM}><DecimalField className="h-8 w-20" placeholder="0" value={f.rabatt} onChange={set("rabatt")} error={err.rabatt} onBlur={() => f.rabatt && check("rabatt", RABATT_OPTS)} onKeyDown={onKey}
        aria-label="Rabatt in Prozent der neuen Position" testId="newpos-rabatt" /></td>
      <td className={`${TD_STATIC} text-slate-500`}>19%</td>
      <td className={`${TD_STATIC} text-slate-500`}>—</td>
      <td className={TD}></td>
      <td className={TD}>
        <Button size="sm" className="h-8" onClick={submit} disabled={saving} aria-label="Position hinzufügen" data-testid="newpos-add">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </td>
    </tr>
  );
}
